// ============================================================
// v8 — the auto-cost engine (BOLT-011).
//
// A card's hider-draw cost is derived by Monte-Carlo: how much can
// it shrink the zone? The naive "mean cut %" does NOT reproduce the
// shipped radar ladder (1000→1 … 100→4): a small radar usually
// answers OUTSIDE (a tiny cut) but is decisive when it bites, so its
// mean cut is LOWER than a big radar's. The faithful driver is the
// card's LOCALIZATION POWER — a high quantile of the cut with the
// seeker sampled NEAR the hider (a seeker plays a tight radar when
// they suspect proximity) — mapped through -log10(1 - power), the
// information content of the confinement. Calibrated so radar
// 1000/500/250/100 reproduce 1/2/3/4 on the seed boundary.
// ============================================================
import { area, booleanPointInPolygon, destination, point as turfPoint } from '@turf/turf';
import type { Feature, Polygon, MultiPolygon, Position } from 'geojson';
import { DEFAULT_BOUNDARY } from './packs/boundaries';
import { resolveBuiltin } from './resolveCard';
import { runCardScriptSync } from './sandbox/cardSandbox';
import { ft } from './constants';
import { mulberry32 } from './rng';
import type { CardDef } from './pack';
import type { LngLat, Zone } from './protocol';

type Poly = Feature<Polygon | MultiPolygon>;

export interface CostReport {
  cost: number; // integer hider-draw count
  powerPct: number; // the localization-power cut driving the cost
  meanCutPct: number;
  variancePct: number; // variance of the per-sample cut
  bits: number; // mean log2 area-reduction (information)
  samples: number;
  pinned?: number; // def.cost, if the author pinned one
  pinnedDivergence?: number; // |cost - pinned|, flagged when large
}

export interface CostOptions {
  boundary?: Zone;
  samples?: number;
  seed?: number;
  quantile?: number; // power quantile (default 0.9)
  k?: number; // calibration constant
  maxCost?: number;
}

const DEFAULT_SAMPLES = 200;
const DEFAULT_QUANTILE = 0.9;
const DEFAULT_K = 1.6; // calibrated so the radar ladder lands on 1/2/3/4
const DEFAULT_MAX_COST = 8;
const PINNED_DIVERGENCE_FLAG = 2;

const pt = (p: LngLat) => turfPoint([p.lng, p.lat]);

function bbox(b: Zone): [number, number, number, number] {
  const rings = (b.geometry.type === 'Polygon' ? b.geometry.coordinates : b.geometry.coordinates.flat()) as Position[][];
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const r of rings) for (const [lng, lat] of r) {
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  }
  return [minLng, minLat, maxLng, maxLat];
}

function randInside(b: Zone, bb: [number, number, number, number], rand: () => number): LngLat {
  for (let i = 0; i < 200; i++) {
    const lng = bb[0] + rand() * (bb[2] - bb[0]);
    const lat = bb[1] + rand() * (bb[3] - bb[1]);
    if (booleanPointInPolygon([lng, lat], b as Poly)) return { lat, lng };
  }
  return { lat: (bb[1] + bb[3]) / 2, lng: (bb[0] + bb[2]) / 2 };
}

/** Sample a seeker biased toward H (a tight question is played when close). */
function seekerNearH(H: LngLat, rMaxM: number, b: Zone, rand: () => number): LngLat {
  const d = rMaxM * rand() * rand(); // bias toward 0 → many near-H samples
  for (let i = 0; i < 8; i++) {
    const brng = rand() * 360;
    const s = destination(pt(H), d, brng, { units: 'meters' }).geometry.coordinates as [number, number];
    if (booleanPointInPolygon([s[0], s[1]], b as Poly)) return { lat: s[1], lng: s[0] };
  }
  return H;
}

/** Area of Z after the card resolves for one (S, H). */
function afterArea(def: CardDef, S: LngLat, H: LngLat, Z: Zone, rand: () => number): number {
  if (def.behavior.kind === 'script') {
    const r = runCardScriptSync(def.behavior.source, { S, H, Z, boundary: Z, seed: 1 });
    return r.ok && r.zone ? area(r.zone) : area(Z);
  }
  const b = def.behavior;
  switch (b.ref) {
    case 'radar':
    case 'direction':
    case 'quadrants': // v11 §2K — same inputs as direction
      return area(resolveBuiltin(def, { S, H, Z }).zone);
    case 'borders': // v11 §2L — the coster's zone doubles as the boundary
      return area(resolveBuiltin(def, { S, H, Z, boundary: Z as unknown as import('./protocol').Boundary }).zone);
    case 'thermometer': {
      const minM = ft(typeof b.params?.minFt === 'number' ? b.params.minFt : 100);
      const end = destination(pt(S), minM * 1.5, rand() * 360, { units: 'meters' }).geometry.coordinates as [number, number];
      return area(resolveBuiltin(def, { S, H, Z, Pstart: S, Pend: { lat: end[1], lng: end[0] } }).zone);
    }
    case 'photoPenalty':
      return area(resolveBuiltin(def, { S, H, Z, photoRadiusM: ft(250) }).zone);
    case 'hider':
      return area(Z);
    case 'airhorn': // v16 BOLT-072: reveals by ear — no zone change; cost is pinned, never auto-derived
      return area(Z);
    case 'nearest':
      // v24 BOLT-118: a landmark card's power swings with feature DENSITY (a
      // park-dense map cuts hard, a sparse one barely at all), so it cannot be
      // auto-costed from synthetic actors. The cost is pinned in CORE_DECK,
      // tuned to a typical map, like the airhorn's.
      return area(Z);
  }
}

function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))));
  return sorted[idx];
}

/** Auto-derive a card's hider-draw cost from its localization power. */
export function costCard(def: CardDef, opts: CostOptions = {}): CostReport {
  const Z = (opts.boundary ?? (DEFAULT_BOUNDARY.boundary as Zone)) as Zone;
  const n = opts.samples ?? DEFAULT_SAMPLES;
  const k = opts.k ?? DEFAULT_K;
  const q = opts.quantile ?? DEFAULT_QUANTILE;
  const maxCost = opts.maxCost ?? DEFAULT_MAX_COST;
  const rand = mulberry32(((opts.seed ?? 4242) >>> 0) || 1);

  const z0 = area(Z);
  const bb = bbox(Z);
  const rMax = Math.sqrt(z0 / Math.PI); // the zone's characteristic radius

  const cuts: number[] = [];
  const bitsArr: number[] = [];
  for (let i = 0; i < n; i++) {
    const H = randInside(Z, bb, rand);
    const S = seekerNearH(H, rMax, Z, rand);
    const after = afterArea(def, S, H, Z, rand);
    const cut = z0 > 0 ? Math.max(0, Math.min(100, ((z0 - after) / z0) * 100)) : 0;
    cuts.push(cut);
    bitsArr.push(after > 0 ? Math.log2(z0 / after) : 0);
  }

  const mean = cuts.reduce((a, b) => a + b, 0) / (cuts.length || 1);
  const variance = cuts.reduce((a, b) => a + (b - mean) ** 2, 0) / (cuts.length || 1);
  const bits = bitsArr.reduce((a, b) => a + b, 0) / (bitsArr.length || 1);
  const power = quantile([...cuts].sort((a, b) => a - b), q);

  // cost = K · information content of the tightest confinement, clamped.
  const frac = Math.max(1e-4, 1 - power / 100); // remaining fraction at power
  const raw = k * -Math.log10(frac);
  const cost = Math.max(1, Math.min(maxCost, Math.round(raw)));

  const report: CostReport = { cost, powerPct: power, meanCutPct: mean, variancePct: variance, bits, samples: n };
  if (typeof def.cost === 'number') {
    report.pinned = def.cost;
    report.pinnedDivergence = Math.abs(cost - def.cost);
  }
  return report;
}
